export type GlobalHeaderEntry = {
  key: string;
  value: string;
};

export type GlobalHeaderSource = {
  key?: string | null;
  value?: string | null;
};

export type GlobalHeadersSaveBody = {
  globalHeaders: GlobalHeaderEntry[];
  limitGlobalHeadersByScope: boolean;
};

export const toGlobalHeaderEntries = (
  headers: readonly GlobalHeaderSource[] | null | undefined,
): GlobalHeaderEntry[] =>
  (headers ?? []).map((header) => ({
    key: header.key ?? '',
    value: header.value ?? '',
  }));

export const hasGlobalHeaderChanges = ({
  headers,
  limitByScope,
  originalHeaders,
  originalLimitByScope,
}: {
  headers: readonly GlobalHeaderEntry[];
  limitByScope: boolean;
  originalHeaders: readonly GlobalHeaderSource[] | null | undefined;
  originalLimitByScope: boolean;
}): boolean => {
  const original = originalHeaders ?? [];

  if (limitByScope !== originalLimitByScope) {
    return true;
  }

  if (headers.length !== original.length) {
    return true;
  }

  return headers.some(
    (header, index) =>
      header.key !== (original[index]?.key ?? '') ||
      header.value !== (original[index]?.value ?? ''),
  );
};

export const buildGlobalHeadersSaveBody = ({
  headers,
  limitByScope,
}: {
  headers: readonly GlobalHeaderEntry[];
  limitByScope: boolean;
}): GlobalHeadersSaveBody => ({
  globalHeaders: headers
    .filter((header) => header.key.trim())
    .map((header) => ({ key: header.key.trim(), value: header.value })),
  limitGlobalHeadersByScope: limitByScope,
});

export const removeVisibleHeaderIndex = ({
  visibleIndexes,
  removedIndex,
}: {
  visibleIndexes: ReadonlySet<number>;
  removedIndex: number;
}): Set<number> =>
  new Set(
    [...visibleIndexes]
      .filter((index) => index !== removedIndex)
      .map((index) => (index > removedIndex ? index - 1 : index)),
  );

export const toggleVisibleHeaderIndex = ({
  visibleIndexes,
  index,
}: {
  visibleIndexes: ReadonlySet<number>;
  index: number;
}): Set<number> =>
  visibleIndexes.has(index)
    ? new Set([...visibleIndexes].filter((visible) => visible !== index))
    : new Set([...visibleIndexes, index]);
